import { ArrowBack } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { VehicleDamageDetailsDto } from 'dtos'
import { useNavigate, useParams } from 'react-router-dom'

interface DamageDetailsHeroCardProps {
  damage: VehicleDamageDetailsDto
}

export default function DamageDetailsHeroCard({ damage }: DamageDetailsHeroCardProps) {
  const navigate = useNavigate()
  const { vehicleGuid } = useParams()

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container wrap='nowrap' alignItems='center' justifyContent='space-between'>
            <Grid item>
              <Typography fontWeight='bold' variant='h5' sx={{ pr: 1 }}>
                Stock #{damage.importerStockNumber ?? '-'}
              </Typography>
            </Grid>

            <Grid item>
              <Button
                color='primary'
                disabled={!vehicleGuid}
                onClick={() => {
                  if (vehicleGuid) {
                    navigate(`/vehicles/${encodeURIComponent(vehicleGuid)}/damage`, { replace: true })
                  }
                }}
                startIcon={<ArrowBack />}
                variant='text'
              >
                BACK
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <LabelField label='VIN' value={damage.vin} />
              </Grid>

              <Grid item xs={12} sm={3}>
                <LabelField label='Year' value={damage.year} />
              </Grid>

              <Grid item xs={12} sm={3}>
                <LabelField label='Make' value={damage.make} />
              </Grid>

              <Grid item xs={12} sm={3}>
                <LabelField label='Model' value={damage.model} />
              </Grid>

              <Grid item xs={12} sm={3}>
                <LabelField label='Exporter' value={damage.exporterCompany?.name} />
              </Grid>

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`**Stock Number:**

The value is displayed if the vehicle is completed and locked.

The format is a four-digit number followed by a hyphen (-) and the last three digits of the buyer company code for the vehicle (e.g., 1234-ABC).
If the vehicle is not completed and locked, the stock number will display as “-”.

**BACK Button:**

Clicking the BACK button will take the user to the previous To-Do List screen.

**CANCEL IMPORT Button:**

Clicking the CANCEL IMPORT button will stop the vehicle import process. This action will:

- Add a "Canceled" tag to the vehicle record, visible in the To-Do List.

- Append a label to the vehicle record that reads: "Canceled on {date} by {user}."

The CANCEL IMPORT button is disabled under the following conditions:

- The vehicle is locked.

- The vehicle is associated with a PAPS.

- The vehicle has at least one Check-In Photo uploaded.

The button is only accessible to users with the VTS2 Access “Cancel Import Capability”.

**EDIT VIN Button:**

Clicking the EDIT VIN button opens the Add/Edit VIN dialog.

**EDIT PARKING Button:**

Clicking the EDIT PARKING button opens the Edit Parking Space dialog.

**VIN:**

Displays the vehicle's VIN as entered in the Add/Edit VIN dialog.

**Year:**

Displays the vehicle's year as entered in the Add/Edit VIN dialog.

**Make:**

Displays the vehicle's make as entered in the Add/Edit VIN dialog.

**Model:**

Displays the vehicle's model as entered in the Add/Edit VIN dialog.

**Exporter:**

Displays the exporter associated with the vehicle, as selected in the Add/Edit VIN dialog.

**Parking Space:**

Displays the vehicle's parking space location as entered in the Edit Parking dialog.`}
                </GreenText>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
